import React, { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { TextField, IconButton, SxProps, useTheme, useMediaQuery } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { TimePicker as MuiTimePicker, TimePickerProps } from '@mui/x-date-pickers'
import { makeStyles } from 'tss-react/mui'
import { BZTime } from '@e3dc-react/icons'
import { Moment } from '@e3dc-react/shell/Libs/moment'

const useStyles = makeStyles()(theme => ({
  timePicker: {
    '& .MuiSvgIcon-root': {
      color: theme.palette.grey.grey00,
      cursor: 'pointer',
    },
  },
}))

type Props = {
  name: string
  /** transform the value from time picker to a suitable format before passing to onChange function  */
  onTransform?: (value: Moment | null) => Date | string | null
  textFieldStyles?: SxProps<Theme>
  fullWidth?: boolean
} & Omit<Partial<TimePickerProps<Moment, Moment>>, 'name' | 'type'>

/**
 * MUI TimePicker with react hook form
 */
const TimePicker: React.FC<Props> = ({ name, onTransform, textFieldStyles, ...props }) => {
  const { control } = useFormContext()
  const [isOpen, setIsOpen] = useState(false)
  const { classes } = useStyles()
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const applyFullWidth = props.fullWidth || downSm

  const toggleOpen = (): void => setIsOpen(prev => !prev)
  const handleClose = (): void => setIsOpen(false)

  const inputProps = {
    endAdornment: (
      <IconButton onClick={toggleOpen} className={classes.timePicker}>
        <BZTime />
      </IconButton>
    ),
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, ...field }, fieldState: { error } }) => (
        <MuiTimePicker
          {...field}
          {...props}
          onChange={value => {
            const transformedValue = onTransform?.(value) ?? value
            onChange(transformedValue)
          }}
          renderInput={params => (
            <TextField
              {...params}
              error={!!error}
              helperText={error?.message}
              InputProps={inputProps}
              sx={textFieldStyles}
              fullWidth={applyFullWidth}
            />
          )}
          onClose={handleClose}
          open={isOpen}
        />
      )}
    />
  )
}

TimePicker.displayName = 'TimePicker'

export default TimePicker
